"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import { useEffect } from "react";
import { ConstantsDriftBanner } from "@/components/features/staking/ConstantsDriftBanner";
import { useDaoYear } from "@/hooks/stake/useDaoYear";
import { useGlobalStakeStats } from "@/hooks/stake/useGlobalStakeStats";
import { useStakeConstants } from "@/hooks/stake/useStakeConstants";
import { useTxToasts } from "@/hooks/stake/useTxToasts";
import { useUvmHolderCount } from "@/hooks/stake/useUvmHolderCount";
import { STAKE_CONSTANTS } from "@/lib/stake/stake-constants";
import { DaoProgressCard } from "./DaoProgressCard";
import { GlobalStatsSkeleton } from "./GlobalStatsSkeleton";
import { StakedCountBoxes } from "./StakedCountBoxes";
import { UvmHeadlineBar } from "./UvmHeadlineBar";
import { UvmTotalsPanel } from "./UvmTotalsPanel";

const DECAY_PERCENT = Number(STAKE_CONSTANTS.REWARD_DECAY_FACTOR);

/** Tab 2 content — global staking stats (سپرده اران دائو). */
export const GlobalStatsTab: React.FC = () => {
  const t = useTranslations("stake");
  const stats = useGlobalStakeStats();
  const dao = useDaoYear();
  const constants = useStakeConstants();
  const holders = useUvmHolderCount();
  const { showError } = useTxToasts();

  useEffect(() => {
    if (stats.isError) {
      // Pass undefined err so the generic stats-error fallback is shown (the
      // raw multicall error message is not user-facing).
      showError(undefined, t("globalStats.statsError"));
    }
  }, [stats.isError, showError, t]);

  if (constants.isError) {
    return (
      <ConstantsDriftBanner
        driftedKey={constants.error?.message ?? "unknown"}
        expected={0n}
        actual={0n}
      />
    );
  }

  if (!dao || (!stats.data && !stats.isError)) {
    return <GlobalStatsSkeleton />;
  }

  return (
    <div className="space-y-3 pb-8">
      {/* Prefer the real token-holder count from the indexer; until that
          setting exists, fall back to the on-chain staker count so the bar is
          never empty. */}
      <UvmHeadlineBar
        holderCount={holders.count ?? stats.data?.stakeholderCount}
      />
      <div className="grid grid-cols-2 gap-2">
        {/* RTL source order: white NFT/DNM boxes first (rightmost), dark panel left */}
        <StakedCountBoxes stats={stats.data} />
        <UvmTotalsPanel stats={stats.data} />
      </div>
      <DaoProgressCard
        currentYear={dao.currentYear}
        remaining={dao.remaining}
        endSec={dao.boundaries(dao.currentYear).end}
        decayPercent={DECAY_PERCENT}
      />
    </div>
  );
};
